import { useState } from "react";
import { FiCopy } from "react-icons/fi";
import { FaUsers, FaChair, FaTrash, FaChevronDown, FaUser } from "react-icons/fa";
import FamilyMemberRsvpDropdown from "./FamilyMemberRsvpDropdown";
import FamilyTableDropdown from "./FamilyTableDropdown";

const capitalizeWords = (str) =>
    str?.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const getFamilyRsvpSummary = (family) => {
    const members = family.familyMembers || [];
    const attending = members.filter(m => m.attending === true).length;
    const notAttending = members.filter(m => m.attending === false).length;
    const pending = members.filter(m => m.attending === null || m.attending === undefined).length;
    return { attending, notAttending, pending, total: members.length };
};

const getFamilyBadge = (summary) => {
    if (summary.pending === summary.total) {
        return {
            label: 'Nije odgovorio',
            color: 'bg-gray-100 text-gray-500 border-gray-200',
            dot: 'bg-gray-400',
        };
    }

    if (summary.pending === 0) {
        if (summary.attending === 0) {
            return {
                label: 'Niko ne dolazi',
                color: 'bg-red-50 text-red-600 border-red-200',
                dot: 'bg-red-400',
            };
        }
        return {
            label: `Dolazi ${summary.attending}/${summary.total}`,
            color: 'bg-green-50 text-green-700 border-green-200',
            dot: 'bg-green-500',
        };
    }

    const parts = [];
    if (summary.attending > 0) parts.push(`${summary.attending} da`);
    if (summary.notAttending > 0) parts.push(`${summary.notAttending} ne`);
    if (summary.pending > 0) parts.push(`${summary.pending} čeka`);

    return {
        label: parts.join(', '),
        color: 'bg-amber-50 text-amber-700 border-amber-200',
        dot: 'bg-amber-400',
    };
};

const FamilyCard = ({ family, tables, onTableSelect, onDelete, onCopy, onMemberStatusChange }) => {
    const [expanded, setExpanded] = useState(false);

    const summary = getFamilyRsvpSummary(family);
    const badge = getFamilyBadge(summary);

    const currentTableName = (() => {
        if (!family.reservedTableId) return null;
        const id = family.reservedTableId?._id || family.reservedTableId;
        return tables.find(t => t._id === id)?.name || family.reservedTableId?.name;
    })();

    return (
        <div className={`bg-white border-2 rounded-2xl shadow-sm overflow-visible transition-all duration-200 ${expanded ? 'border-rose-300 shadow-md' : 'border-rose-100 hover:border-rose-200 hover:shadow'}`}>
            <div
                onClick={() => setExpanded(e => !e)}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
            >
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${badge.dot}`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <FaUsers className="text-rose-500 text-xs flex-shrink-0" />
                        <p className="font-semibold text-gray-800 truncate capitalize text-sm leading-tight">
                            {capitalizeWords(family.fullName)}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${badge.color}`}>
                            {badge.label}
                        </span>
                        {currentTableName && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 font-medium capitalize">
                                <FaChair className="text-xs" /> {currentTableName}
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-500 border border-rose-100 font-medium">
                            <FaUsers className="text-xs" /> {summary.total}
                        </span>
                    </div>
                </div>
                <FaChevronDown className={`text-gray-400 text-sm flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            </div>

            {expanded && (
                <div className="px-4 pb-4 border-t border-rose-100 pt-3 flex flex-col gap-3">
                    <div className="bg-rose-50 border border-rose-100 rounded-xl p-3">
                        <p className="text-xs font-semibold text-rose-600 mb-3 flex items-center gap-1.5">
                            <FaUsers className="text-xs" /> Članovi obitelji ({summary.total})
                        </p>
                        <div className="flex flex-col gap-2">
                            {family.familyMembers.map((member, idx) => (
                                <div
                                    key={`${member.name}-${idx}`}
                                    className="flex items-center justify-between gap-2 bg-white border border-rose-100 rounded-lg px-3 py-2"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <FaUser className="text-gray-400 flex-shrink-0 text-xs" />
                                        <span className="text-sm capitalize text-gray-800 truncate">
                                            {capitalizeWords(member.name)}
                                        </span>
                                    </div>
                                    <FamilyMemberRsvpDropdown
                                        member={member}
                                        memberIndex={idx}
                                        onSelect={(memberIndex, attending) =>
                                            onMemberStatusChange(family._id, memberIndex, attending, member.name)
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1.5">Dodijeli stol</p>
                        <FamilyTableDropdown family={family} tables={tables} onSelect={onTableSelect} />
                    </div>

                    <div className="flex gap-2 pt-1">
                        <button
                            onClick={() => onCopy(family)}
                            className="flex-1 flex items-center justify-center gap-1.5 bg-gray-800 text-white text-xs font-medium py-2.5 rounded-xl hover:bg-gray-700 transition"
                        >
                            <FiCopy className="text-xs" /> Kopiraj link
                        </button>
                        <button
                            onClick={() => onDelete(family._id)}
                            className="flex items-center justify-center w-10 bg-white border border-red-200 text-red-500 rounded-xl hover:bg-red-50 transition"
                        >
                            <FaTrash className="text-xs" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FamilyCard;
